import React, { useCallback, useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { GridBackground } from '../components/ui/GridBackground';
// 9-slide executive overview (~5–8 minutes)
import { Slide01Hero } from '../components/slides/Slide01Hero';
import { Slide02Problem } from '../components/slides/Slide02Problem';
import { Slide04Layers } from '../components/slides/Slide04Layers';
import { Slide07Behavioral } from '../components/slides/Slide07Behavioral';
import { Slide10Dashboard } from '../components/slides/Slide10Dashboard';
import { Slide12HiddenValue } from '../components/slides/Slide12HiddenValue';
import { Slide16Capability } from '../components/slides/Slide16Capability';
import { Slide18TwoPaths } from '../components/slides/Slide18TwoPaths';
import { Slide12Closing } from '../components/slides/Slide12Closing';
const SLIDES = [Slide01Hero, Slide02Problem, Slide04Layers, Slide07Behavioral, Slide10Dashboard, Slide12HiddenValue, Slide16Capability, Slide18TwoPaths, Slide12Closing // 9. Close — every interaction teaches
];
export function Presentation() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(1);
  const goToNextSlide = useCallback(() => {
    setCurrentSlide(prev => {
      if (prev < SLIDES.length - 1) {
        setDirection(1);
        return prev + 1;
      }
      return prev;
    });
  }, []);
  const goToPrevSlide = useCallback(() => {
    setCurrentSlide(prev => {
      if (prev > 0) {
        setDirection(-1);
        return prev - 1;
      }
      return prev;
    });
  }, []);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        goToNextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevSlide();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNextSlide, goToPrevSlide]);
  const CurrentSlideComponent = SLIDES[currentSlide];
  const slideVariants = {
    enter: (direction: number) => ({
      y: direction > 0 ? 40 : -40,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      y: 0,
      opacity: 1,
      transition: {
        y: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.5
        }
      }
    },
    exit: (direction: number) => ({
      zIndex: 0,
      y: direction < 0 ? 40 : -40,
      opacity: 0,
      transition: {
        y: {
          type: 'spring',
          stiffness: 300,
          damping: 30
        },
        opacity: {
          duration: 0.4
        }
      }
    })
  };
  const isFirst = currentSlide === 0;
  const isLast = currentSlide === SLIDES.length - 1;
  return <div className="relative w-screen h-screen overflow-hidden bg-snakker-bg text-snakker-ink select-none" data-id="element-604">
      <GridBackground data-id="element-605" />

      {/* Header / Logo */}
      <div className="absolute top-8 left-10 z-50 flex items-center gap-2" data-id="element-606">
        <div className="w-6 h-6 rounded-md bg-gradient-to-br from-snakker-primary to-snakker-secondary flex items-center justify-center shadow-[0_2px_10px_rgba(0,133,189,0.3)]" data-id="element-607">
          <div className="w-2 h-2 bg-white rounded-full" data-id="element-608" />
        </div>
        <span className="font-display font-semibold tracking-wide text-lg text-snakker-ink" data-id="element-609">
          Snakker™
        </span>
      </div>

      {/* Slide Counter */}
      <div className="absolute top-8 right-10 z-50 font-mono text-sm text-snakker-muted" data-id="element-610">
        {String(currentSlide + 1).padStart(2, '0')} /{' '}
        {String(SLIDES.length).padStart(2, '0')}
      </div>

      {/* Main Slide Content Area */}
      <div className="relative w-full h-full flex items-center justify-center" data-id="element-611">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-612">
          <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" className="absolute inset-0 flex items-center justify-center w-full h-full p-16" data-id="element-613">
            <CurrentSlideComponent onNext={goToNextSlide} data-id="element-614" />
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Bottom Navigation Bar */}
      <div className="absolute bottom-6 left-0 right-0 z-50 flex items-center justify-center gap-6" data-id="element-615">
        <button onClick={goToPrevSlide} disabled={isFirst} aria-label="Previous slide" className={`group flex items-center gap-1.5 px-4 py-2.5 rounded-full border transition-all ${isFirst ? 'border-snakker-border/50 text-snakker-subtle cursor-not-allowed' : 'border-snakker-ink/20 text-snakker-ink bg-white hover:bg-snakker-ink hover:text-white hover:border-snakker-ink shadow-sm'}`} data-id="element-616">
          <ChevronLeft className="w-4 h-4" data-id="element-617" />
          <span className="text-sm font-medium pr-1" data-id="element-618">Back</span>
        </button>

        <div className="flex items-center gap-1.5" data-id="element-619">
          {SLIDES.map((_, index) => <button key={index} onClick={() => {
          setDirection(index > currentSlide ? 1 : -1);
          setCurrentSlide(index);
        }} aria-label={`Go to slide ${index + 1}`} className="group py-2" data-id="element-620">
              <div className={`h-1 rounded-full transition-all duration-500 ease-out ${index === currentSlide ? 'w-8 bg-snakker-primary' : 'w-1.5 bg-snakker-ink/15 group-hover:bg-snakker-ink/40'}`} data-id="element-621" />
            </button>)}
        </div>

        <button onClick={goToNextSlide} disabled={isLast} aria-label="Next slide" className={`group flex items-center gap-1.5 px-4 py-2.5 rounded-full transition-all ${isLast ? 'bg-snakker-ink/10 text-snakker-subtle cursor-not-allowed' : 'bg-snakker-ink text-white hover:bg-snakker-primary shadow-sm'}`} data-id="element-622">
          <span className="text-sm font-medium pl-1" data-id="element-623">Next</span>
          <ChevronRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-624" />
        </button>
      </div>
    </div>;
}